import { useEffect, useState } from "react";
import { Seat } from "./Seat";
import { PlayingCard, CardSlot } from "./PlayingCard";
import { fmt } from "@/poker/cards";
import { potTotal } from "@/poker/engine";

const pos = (i, n, rx, ry) => {
  const a = ((90 + (i * 360) / n) * Math.PI) / 180;
  return { left: `${50 + rx * Math.cos(a)}%`, top: `${50 + ry * Math.sin(a)}%` };
};

const Badge = ({ label, cls, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-[9px] sm:text-[10px] font-black shadow-md ${cls}`}>
    {label}
  </span>
);

const ResultBanner = ({ game }) => {
  const r = game.result;
  if (!r) return null;
  return (
    <div data-testid="hand-result-banner" className="mt-2 px-3 py-1.5 rounded-lg bg-black/60 backdrop-blur-md border border-amber-500/40 text-center action-pop">
      {r.pots.map((p, i) => (
        <div key={i} className="text-[11px] sm:text-sm text-slate-100">
          <span className="font-bold text-amber-400">{p.winners.map((id) => game.players[id].name).join(" & ")}</span>
          {p.winners.length > 1 ? " split " : p.winners[0] === 0 ? " win " : " wins "}
          <span className="font-mono font-bold">{fmt(p.amount)}</span>
          {p.handName && <span className="text-cyan-300"> · {p.handName}</span>}
        </div>
      ))}
    </div>
  );
};

export const PokerTable = ({ game, felt }) => {
  const n = game.players.length;
  const winners = new Set(game.result?.pots.flatMap((p) => p.winners) || []);
  const pot = potTotal(game);
  const [narrow, setNarrow] = useState(window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const rx = narrow ? 37 : 44;
  return (
    <div data-testid="poker-table-container" className="relative w-full max-w-5xl mx-auto aspect-[3/4] sm:aspect-[16/10] lg:aspect-[16/9]">
      <div className={`absolute inset-[9%_8%] sm:inset-[11%_9%] rounded-[45%] table-rail`}>
        <div className={`absolute inset-[10px] sm:inset-[16px] rounded-[45%] felt felt-${felt} grain`} />
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 pointer-events-none">
        <div data-testid="total-pot-display" className="mb-2 sm:mb-3 px-3 py-1 rounded-full bg-black/50 border border-white/10 font-mono text-xs sm:text-base text-amber-300">
          POT <span className="font-bold text-white">{fmt(game.result ? game.result.pots.reduce((a, p) => a + p.amount, 0) : pot)}</span>
        </div>
        <div data-testid="community-cards-board" className="flex gap-1 sm:gap-2">
          {[0, 1, 2, 3, 4].map((i) =>
            game.board[i] !== undefined ? (
              <PlayingCard key={`${game.handNum}-${i}`} card={game.board[i]} size="md" delay={i < 3 ? i * 110 : 0} testId={`board-card-${i}`} />
            ) : (
              <CardSlot key={i} size="md" testId={`board-slot-${i}`} />
            ),
          )}
        </div>
        <ResultBanner game={game} />
      </div>
      {game.players.map((p, i) => {
        const betPos = i === 0 ? { left: "36%", top: "76%" } : pos(i, n, narrow ? 26 : 30, 25);
        return (
          <div key={p.id}>
            <div className="absolute z-20 -translate-x-1/2 -translate-y-1/2" style={pos(i, n, rx, i === 0 ? 40 : 43)}>
              <Seat compact={narrow && n >= 7} p={p} active={game.toAct === i && game.phase === "betting"} winner={winners.has(i)} isDealerHand={game.handNum} />
            </div>
            <div className="absolute z-[25] -translate-x-1/2 -translate-y-1/2 flex items-center gap-1" style={betPos}>
              {game.dealer === i && !p.out && <Badge label="D" cls="bg-white text-black" testId="dealer-button" />}
              {game.sbIdx === i && game.sbIdx !== game.dealer && !p.out && <Badge label="SB" cls="bg-cyan-500 text-black" testId="sb-badge" />}
              {game.bbIdx === i && !p.out && <Badge label="BB" cls="bg-amber-500 text-black" testId="bb-badge" />}
              {p.bet > 0 && (
                <span data-testid={`seat-${i}-bet`} className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/60 font-mono text-[10px] sm:text-xs font-bold text-white chip-in">
                  <span className="chip" />{fmt(p.bet)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
